import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowLeft, FiHome, FiUsers, FiPackage, FiCalendar, FiActivity, FiTrendingUp, FiBarChart2, FiTarget, FiZap, FiAward, FiMenu, FiX, FiArrowUp } from 'react-icons/fi';

// Type assertion helper for icons to work with React 19
const Icon = ({ component: Component, ...props }: { component: any; [key: string]: any }) => {
  return <Component {...props} />;
};

const Container = styled.div`
  min-height: 100vh;
  background: var(--bg-primary);
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  font-family: 'Poppins', sans-serif;
  color: var(--text-gray);
  position: relative;
  display: flex;
  gap: 2rem;
  
  @media (max-width: 1024px) {
    flex-direction: column;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    gap: 1.5rem;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
`;

const BurgerButton = styled.button`
  position: fixed;
  top: 2rem;
  left: 2rem;
  z-index: 1001;
  background: var(--accent-orange);
  color: white;
  border: none;
  border-radius: 8px;
  width: 50px;
  height: 50px;
  display: none;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  font-size: 1.5rem;

  &:hover {
    background: var(--accent-orange-dark);
    transform: scale(1.05);
  }

  @media (max-width: 1024px) {
    display: flex;
    top: 1rem;
    left: 1rem;
    width: 45px;
    height: 45px;
    font-size: 1.3rem;
  }
`;

const Sidebar = styled.div<{ isOpen: boolean }>`
  width: 280px;
  position: sticky;
  top: 2rem;
  height: fit-content;
  max-height: calc(100vh - 4rem);
  overflow-y: auto;
  background: var(--bg-sidebar);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
  
  @media (max-width: 1024px) {
    position: fixed;
    top: 0;
    left: 0;
    width: 320px;
    height: 100vh;
    border-radius: 0;
    z-index: 1000;
    transform: translateX(${props => props.isOpen ? '0' : '-100%'});
    transition: transform 0.3s ease;
    max-height: 100vh;
  }
  
  @media (max-width: 480px) {
    width: 280px;
    padding: 1.5rem 1rem;
  }
`;

const Overlay = styled.div<{ isOpen: boolean }>`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: ${props => props.isOpen ? '1' : '0'};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  
  @media (max-width: 1024px) {
    display: block;
  }
`;

const TocTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 900;
  color: var(--accent-orange);
  margin-bottom: 1rem;
  font-family: 'Poppins', sans-serif;
  letter-spacing: -0.02em;

  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin-bottom: 0.75rem;
  }
`;

const TocList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const TocItem = styled.li`
  margin-bottom: 0.75rem;
`;

const TocLink = styled.a`
  display: block;
  padding: 0.5rem 0.75rem;
  color: var(--text-gray);
  text-decoration: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.2s;
  cursor: pointer;
  
  &:hover {
    background: rgba(255, 150, 0, 0.1);
    color: var(--accent-orange);
  }
  
  &.active {
    background: rgba(255, 150, 0, 0.15);
    color: var(--accent-orange);
    font-weight: 700;
  }

  @media (max-width: 480px) {
    padding: 0.4rem 0.6rem;
    font-size: 0.85rem;
  }
`;

const TocSubItem = styled(TocItem)`
  margin-left: 1rem;
  margin-bottom: 0.5rem;

  @media (max-width: 480px) {
    margin-left: 0.75rem;
  }
`;

const TocSubLink = styled(TocLink)`
  font-size: 0.85rem;
  font-weight: 500;

  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 3rem;
  flex-wrap: wrap;
  gap: 1rem;
  position: relative;
  width: 100%;
  
  @media (max-width: 768px) {
    margin-bottom: 2rem;
    flex-direction: column;
    align-items: flex-start;
  }

  @media (max-width: 480px) {
    margin-bottom: 1.5rem;
    gap: 0.75rem;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: var(--bg-sidebar);
  border: 2px solid var(--card-border);
  border-radius: 8px;
  color: var(--text-gray);
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Poppins', sans-serif;
  white-space: nowrap;
  
  &:hover {
    background: var(--accent-orange);
    color: white;
    border-color: var(--accent-orange);
  }

  @media (max-width: 1024px) {
    margin-left: 56px;
  }

  @media (max-width: 768px) {
    padding: 0.6rem 1.2rem;
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 900;
  margin: 0;
  font-family: 'Poppins', sans-serif;
  letter-spacing: -0.02em;
  text-align: right;
  flex: 1;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    text-align: left;
    width: 100%;
  }

  @media (max-width: 480px) {
    font-size: 1.75rem;
    line-height: 1.2;
  }
`;

const Sj = styled.span`
  color: var(--accent-orange);
  font-weight: 900;
`;

const Mate = styled.span`
  color: var(--text-dark);
  font-weight: 900;
`;

const Section = styled.section`
  margin-bottom: 4rem;
  background: var(--bg-sidebar);
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-bottom: 2.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 900;
  color: var(--accent-orange);
  margin-bottom: 1.5rem;
  font-family: 'Poppins', sans-serif;
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
  text-align: left;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }
`;

const SubSectionTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 900;
  color: var(--text-dark);
  margin-top: 2rem;
  margin-bottom: 1rem;
  font-family: 'Poppins', sans-serif;
  text-align: left;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-top: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin-top: 1.25rem;
  }
`;

const Paragraph = styled.p`
  font-size: 1rem;
  line-height: 1.8;
  margin-bottom: 1.5rem;
  color: var(--text-gray);
  text-align: left;
  max-width: 100%;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.6;
  }
`;

const StepList = styled.ol`
  list-style: none;
  counter-reset: step-counter;
  padding: 0;
  margin: 2rem auto;
  max-width: 700px;
  width: 100%;
`;

const StepItem = styled.li`
  counter-increment: step-counter;
  margin-bottom: 2.5rem;
  padding-left: 3.5rem;
  position: relative;
  
  &::before {
    content: counter(step-counter);
    position: absolute;
    left: 0;
    top: 0;
    width: 2.5rem;
    height: 2.5rem;
    background: var(--accent-orange);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 900;
    font-size: 1.2rem;
  }
  
  @media (max-width: 768px) {
    padding-left: 3rem;
    margin-bottom: 2rem;
    
    &::before {
      width: 2rem;
      height: 2rem;
      font-size: 1rem;
    }
  }

  @media (max-width: 480px) {
    padding-left: 2.5rem;
    margin-bottom: 1.5rem;
    
    &::before {
      width: 1.75rem;
      height: 1.75rem;
      font-size: 0.9rem;
    }
  }
`;

const StepTitle = styled.h4`
  font-size: 1.2rem;
  font-weight: 900;
  color: var(--text-dark);
  margin-bottom: 0.75rem;
  font-family: 'Poppins', sans-serif;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }
`;

const StepDescription = styled.p`
  font-size: 1rem;
  line-height: 1.8;
  color: var(--text-gray);
  margin-bottom: 0.5rem;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.6;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1.5rem 0;
`;

const FeatureItem = styled.li`
  padding: 1rem;
  margin-bottom: 1rem;
  background: var(--bg-primary);
  border-radius: 8px;
  border-left: 4px solid var(--accent-orange);
  padding-left: 1.5rem;
  text-align: left;

  @media (max-width: 768px) {
    padding: 0.875rem;
    padding-left: 1.25rem;
    margin-bottom: 0.875rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    padding-left: 1rem;
    margin-bottom: 0.75rem;
    border-left-width: 3px;
  }
`;

const FeatureTitle = styled.div`
  font-size: 1.1rem;
  font-weight: 900;
  color: var(--text-dark);
  margin-bottom: 0.5rem;
  font-family: 'Poppins', sans-serif;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 1.05rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 0.375rem;
  }
`;

const FeatureDescription = styled.div`
  font-size: 0.95rem;
  line-height: 1.6;
  color: var(--text-gray);
  text-align: left;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    line-height: 1.5;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
    line-height: 1.4;
  }
`;

const HighlightBox = styled.div`
  background: rgba(255, 150, 0, 0.1);
  border-left: 4px solid var(--accent-orange);
  padding: 1.5rem;
  border-radius: 8px;
  margin: 1.5rem 0;
`;

const HighlightText = styled.p`
  font-size: 1rem;
  line-height: 1.8;
  color: var(--text-gray);
  margin: 0;
  font-weight: 600;
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: 0.5rem;
  color: var(--accent-orange);
  flex-shrink: 0;

  @media (max-width: 480px) {
    margin-right: 0.375rem;
  }
`;

const SectionWithId = styled(Section)`
  scroll-margin-top: 2rem;
`;

const ScrollToTopButton = styled.button`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 50px;
  height: 50px;
  background: var(--accent-orange);
  color: white;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  z-index: 999;
  font-size: 1.5rem;
  opacity: 0;
  visibility: hidden;

  &.visible {
    opacity: 1;
    visibility: visible;
  }

  &:hover {
    background: var(--accent-orange-dark);
    transform: translateY(-3px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
    bottom: 1.5rem;
    right: 1.5rem;
    font-size: 1.3rem;
  }

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    bottom: 1rem;
    right: 1rem;
    font-size: 1.1rem;
  }
`;

const HowToStart: React.FC = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    // Go back to previous page
    navigate(-1);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Close menu after clicking a link (only on mobile)
      if (isMobile) {
        setIsMenuOpen(false);
      }
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1024;
      setIsMobile(mobile);
      // Auto-close menu when switching to desktop
      if (!mobile && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    const handleScroll = () => {
      // Show scroll-to-top button when scrolled down more than 300px
      setShowScrollTop(window.scrollY > 300);
    };

    // Handle hash navigation on page load
    const handleHashNavigation = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash) {
        setTimeout(() => {
          scrollToSection(hash);
        }, 300); // Delay to ensure page is fully loaded
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    handleHashNavigation(); // Check for hash on mount
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // On desktop, sidebar is always "open" (visible)
  const sidebarIsOpen = isMobile ? isMenuOpen : true;

  return (
    <Container>
      <BurgerButton onClick={toggleMenu} aria-label="Toggle menu">
        <Icon component={isMenuOpen ? FiX : FiMenu} />
      </BurgerButton>
      <Overlay isOpen={isMenuOpen && isMobile} onClick={toggleMenu} />
      <Sidebar isOpen={sidebarIsOpen}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <TocTitle style={{ margin: 0 }}>Table of Contents</TocTitle>
          {isMobile && (
            <button 
              onClick={toggleMenu}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--text-gray)',
                cursor: 'pointer',
                fontSize: '1.5rem',
                padding: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 150, 0, 0.1)';
                e.currentTarget.style.color = 'var(--accent-orange)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--text-gray)';
              }}
              aria-label="Close menu"
            >
              <Icon component={FiX} />
            </button>
          )}
        </div>
        <TocList>
          <TocItem>
            <TocLink onClick={() => scrollToSection('getting-started')}>
              Getting Started
            </TocLink>
          </TocItem>
          <TocItem>
            <TocLink onClick={() => scrollToSection('connecting-accounts')}>
              Connecting Accounts
            </TocLink>
          </TocItem>
          <TocItem>
            <TocLink onClick={() => scrollToSection('home-dashboard')}>
              Home Dashboard
            </TocLink>
          </TocItem>
          <TocItem>
            <TocLink onClick={() => scrollToSection('athletes')}>
              Athletes
            </TocLink>
          </TocItem>
          <TocItem>
            <TocLink onClick={() => scrollToSection('training-sessions')}>
              Training Sessions
            </TocLink>
          </TocItem>
          <TocItem>
            <TocLink onClick={() => scrollToSection('competition-sessions')}>
              Competition Sessions
            </TocLink>
          </TocItem>
          <TocItem>
            <TocLink onClick={() => scrollToSection('equipment')}>
              Equipment
            </TocLink>
          </TocItem>
          <TocItem>
            <TocLink onClick={() => scrollToSection('training-planner')}>
              Training Planner
            </TocLink>
          </TocItem>
          <TocItem>
            <TocLink onClick={() => scrollToSection('banana-cup')}>
              Banana Cup
            </TocLink>
          </TocItem>
          <TocItem>
            <TocLink onClick={() => scrollToSection('analytics')}>
              Analytics
            </TocLink>
            <TocSubItem>
              <TocSubLink onClick={() => scrollToSection('analytics-training')}>
                Training Sessions
              </TocSubLink>
            </TocSubItem>
            <TocSubItem>
              <TocSubLink onClick={() => scrollToSection('analytics-competition')}>
                Competition Sessions
              </TocSubLink>
            </TocSubItem>
            <TocSubItem>
              <TocSubLink onClick={() => scrollToSection('analytics-distance')}>
                Distance
              </TocSubLink>
            </TocSubItem>
            <TocSubItem>
              <TocSubLink onClick={() => scrollToSection('analytics-speed')}>
                Inrun Speed
              </TocSubLink>
            </TocSubItem>
            <TocSubItem>
              <TocSubLink onClick={() => scrollToSection('analytics-weight')}>
                Weight
              </TocSubLink>
            </TocSubItem>
            <TocSubItem>
              <TocSubLink onClick={() => scrollToSection('analytics-equipment')}>
                Equipment
              </TocSubLink>
            </TocSubItem>
            <TocSubItem>
              <TocSubLink onClick={() => scrollToSection('analytics-ground')}>
                Ground Training
              </TocSubLink>
            </TocSubItem>
            <TocSubItem>
              <TocSubLink onClick={() => scrollToSection('analytics-jumper')}>
                Jumper Analytics
              </TocSubLink>
            </TocSubItem>
          </TocItem>
          <TocItem>
            <TocLink onClick={() => scrollToSection('ai-chat')}>
              AI Mate
            </TocLink>
          </TocItem>
          <TocItem>
            <TocLink onClick={() => scrollToSection('tips')}>
              Tips & Best Practices
            </TocLink>
          </TocItem>
        </TocList>
      </Sidebar>

      <ContentWrapper>
        <Header>
          <BackButton onClick={handleBack}>
            <Icon component={FiArrowLeft} /> Back
          </BackButton>
          <Title>How to Start with <Sj>sj</Sj><Mate>Mate</Mate></Title>
        </Header>

      {/* Getting Started Section */}
      <SectionWithId id="getting-started">
        <SectionTitle>
          <IconWrapper><Icon component={FiTarget} /></IconWrapper>
          Getting Started - Quick Start Guide
        </SectionTitle>
        
        <Paragraph>
          Welcome to <strong><Sj>sj</Sj><Mate>Mate</Mate></strong> - your comprehensive ski jumping performance tracking system. 
          This guide will walk you through everything you need to know to get started and make the most of the application.
        </Paragraph>

        <StepList>
          <StepItem>
            <StepTitle>Step 1: Add New Athletes</StepTitle>
            <StepDescription>
              Navigate to the <strong>Athletes</strong> section from the main dashboard and click "Add New Athlete". 
              Enter the athlete's first name, last name, date of birth, club, and gender. This basic information is essential 
              for tracking and organizing your athletes.
            </StepDescription>
          </StepItem>

          <StepItem>
            <StepTitle>Step 2: Create Your First Training Session</StepTitle>
            <StepDescription>
              Go to the <strong>Training Sessions</strong> section and click "START NEW TRAINING SESSION". Fill in the session details: 
              date, start time, end time, location, weather conditions, and select the athletes participating. Choose the appropriate 
              training type:
            </StepDescription>
            <FeatureList>
              <FeatureItem>
                <FeatureTitle>Ski Jumping</FeatureTitle>
                <FeatureDescription>
                  For actual ski jumping training sessions on the hill. You'll record individual jumps with distance, gate, wind, speed, and equipment data.
                </FeatureDescription>
              </FeatureItem>
              <FeatureItem>
                <FeatureTitle>Ground Training</FeatureTitle>
                <FeatureDescription>
                  For off-hill training sessions including coordination, power, speed, stretching, specialized coordination, imitation ski jump, running, and visualization exercises.
                </FeatureDescription>
              </FeatureItem>
              <FeatureItem>
                <FeatureTitle>Cross Country</FeatureTitle>
                <FeatureDescription>
                  For cross country skiing training sessions to track endurance and cardiovascular training.
                </FeatureDescription>
              </FeatureItem>
            </FeatureList>
            <StepDescription style={{ marginTop: '1rem' }}>
              Once you've filled in all the details, click "Start Session" to begin recording your training data.
            </StepDescription>
          </StepItem>

          <StepItem>
            <StepTitle>Step 3.1: Add Jumps (Ski Jumping Sessions)</StepTitle>
            <StepDescription>
              With an active <strong>Ski Jumping</strong> session, click "Import Jump" to record each jump. Enter distance, starting gate, wind, speed, 
              telemark landing, and select the equipment used.
            </StepDescription>
          </StepItem>

          <StepItem>
            <StepTitle>Step 3.2: Add Ground Training Records</StepTitle>
            <StepDescription>
              With an active <strong>Ground Training</strong> session, click "Add Ground Training" to record each exercise. Select the training type 
              (coordination, power, speed, stretching, specialized coordination, imitation ski jump, run, or visualization) and enter the intensity level (1-10). 
              This helps track off-hill training progress and its impact on performance.
            </StepDescription>
          </StepItem>

          <StepItem>
            <StepTitle>Step 4: View Your Data</StepTitle>
            <StepDescription>
              Once you have data recorded, you can view all your added jumps and ground training records in the lists below on the training session page. 
              Each session shows all recorded data, including distances, equipment used, training types, and intensity levels. 
              This allows you to review your training history and track progress over time.
            </StepDescription>
          </StepItem>
        </StepList>
      </SectionWithId>

      {/* Connecting Coach and Athlete Accounts Section */}
      <SectionWithId id="connecting-accounts">
        <SectionTitle>
          <IconWrapper><Icon component={FiUsers} /></IconWrapper>
          Connecting Coach and Athlete Accounts
        </SectionTitle>
        
        <Paragraph>
          For coaches and athletes to work together in <strong><Sj>sj</Sj><Mate>Mate</Mate></strong>, their accounts need to be connected. 
          This allows coaches to view and manage their athletes' data, and athletes to see their performance tracked by their coach. 
          The connection process depends on who creates their account first.
        </Paragraph>

        <SubSectionTitle>Scenario 1: Coach Creates Account First</SubSectionTitle>
        <Paragraph>
          If the coach already has an account and wants to add an athlete, follow these steps:
        </Paragraph>

        <StepList>
          <StepItem>
            <StepTitle>Step 1: Coach Creates Athlete Record</StepTitle>
            <StepDescription>
              The coach logs into their account and navigates to the <strong>Athletes</strong> section. 
              They click "Add New Athlete" and fill in the athlete's information (first name, last name, club, gender). 
              After saving, the system automatically generates a unique <strong>Athlete ID</strong> for this athlete.
            </StepDescription>
          </StepItem>

          <StepItem>
            <StepTitle>Step 2: Coach Shares Athlete ID</StepTitle>
            <StepDescription>
              The coach copies the <strong>Athlete ID</strong> (displayed after creating the athlete record) and shares it with the athlete. 
              This ID is unique to each athlete and is required for the athlete to connect their account.
            </StepDescription>
          </StepItem>

          <StepItem>
            <StepTitle>Step 3: Athlete Creates Account</StepTitle>
            <StepDescription>
              The athlete goes to the sign-up page and selects <strong>"I am a Ski Jumper"</strong> as their role. 
              During the sign-up process, they will be prompted to enter the <strong>Athlete ID</strong> provided by their coach. 
              After entering the ID and completing registration, their account will be automatically linked to the coach's athlete record.
            </StepDescription>
          </StepItem>
        </StepList>

        <SubSectionTitle>Scenario 2: Athlete Creates Account First</SubSectionTitle>
        <Paragraph>
          If the athlete creates their account before the coach adds them, follow these steps:
        </Paragraph>

        <StepList>
          <StepItem>
            <StepTitle>Step 1: Athlete Creates Account</StepTitle>
            <StepDescription>
              The athlete goes to the sign-up page and selects <strong>"I am a Ski Jumper"</strong> as their role. 
              They complete the registration process without entering an Athlete ID (since the coach hasn't created their record yet). 
              After registration, the system automatically generates an <strong>Athlete ID</strong> for the athlete.
            </StepDescription>
          </StepItem>

          <StepItem>
            <StepTitle>Step 2: Athlete Shares Athlete ID with Coach</StepTitle>
            <StepDescription>
              The athlete finds their <strong>Athlete ID</strong> in their account (usually displayed in the Athletes section or Account Settings). 
              They copy this ID and share it with their coach. The Athlete ID is unique and allows the coach to connect to the athlete's existing account.
            </StepDescription>
          </StepItem>

          <StepItem>
            <StepTitle>Step 3: Coach Connects to Athlete</StepTitle>
            <StepDescription>
              The coach logs into their account and navigates to the <strong>Athletes</strong> section. 
              They click "Connect by ID" (or similar button) and enter the Athlete ID provided by the athlete. 
              After submitting, the coach's account will be linked to the athlete's account, and the athlete will appear in the coach's athlete list.
            </StepDescription>
          </StepItem>

          <StepItem>
            <StepTitle>Step 4: Verification</StepTitle>
            <StepDescription>
              After connection, both the coach and athlete should verify the connection:
            </StepDescription>
            <FeatureList>
              <FeatureItem>
                <FeatureDescription>
                  <strong>Coach:</strong> The athlete should now appear in the coach's Athletes list with all their existing data (jumps, equipment, sessions).
                </FeatureDescription>
              </FeatureItem>
              <FeatureItem>
                <FeatureDescription>
                  <strong>Athlete:</strong> The athlete should see that their coach is now connected and can view/manage their data.
                </FeatureDescription>
              </FeatureItem>
            </FeatureList>
          </StepItem>
        </StepList>

        <HighlightBox>
          <HighlightText>
            <strong>⚠️ Important Notes:</strong>
          </HighlightText>
          <FeatureList style={{ marginTop: '1rem', marginBottom: '0' }}>
            <FeatureItem style={{ borderLeft: '4px solid var(--accent-orange)', paddingLeft: '1rem', marginBottom: '0.75rem' }}>
              <FeatureDescription>
                Each Athlete ID can only be linked to one athlete account. Make sure you're using the correct ID.
              </FeatureDescription>
            </FeatureItem>
            <FeatureItem style={{ borderLeft: '4px solid var(--accent-orange)', paddingLeft: '1rem', marginBottom: '0.75rem' }}>
              <FeatureDescription>
                If an athlete is already linked to a coach's athlete record, they cannot link to another coach's record with the same ID.
              </FeatureDescription>
            </FeatureItem>
            <FeatureItem style={{ borderLeft: '4px solid var(--accent-orange)', paddingLeft: '1rem', marginBottom: '0' }}>
              <FeatureDescription>
                Once connected, all existing athlete data (jumps, equipment, sessions) becomes accessible to the coach.
              </FeatureDescription>
            </FeatureItem>
          </FeatureList>
        </HighlightBox>
      </SectionWithId>

      {/* Detailed Feature Explanations */}
      <SectionWithId id="home-dashboard">
        <SectionTitle>
          <IconWrapper><Icon component={FiHome} /></IconWrapper>
          Home Dashboard
        </SectionTitle>
        
        <Paragraph>
          The Home dashboard is your central hub when you log in. Coaches see an overview of their team's performance, 
          while athletes (jumpers) see their personal performance metrics and achievements.
        </Paragraph>

        <SubSectionTitle>Coach Dashboard</SubSectionTitle>
        <Paragraph>
          When logged in as a coach, the Home dashboard provides a quick overview of your team's performance and upcoming activities.
        </Paragraph>

        <SubSectionTitle>Key Metrics</SubSectionTitle>
        <Paragraph>
          At the top of the dashboard, you'll see key metrics that you can customize. Click on the <strong>settings icon</strong> (⚙️) 
          in the top right corner of the dashboard to choose which metrics to display. You can select up to 3 metrics from the following options:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureDescription>
              • Number of Training Sessions
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Number of Ski Jumping Training Sessions
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Number of Ground Training Sessions
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Total Jumps
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Average Distance
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Best Distance
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Total Wins as a Coach
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Number of Competition Sessions
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle>Top Performers and Other Sections</SubSectionTitle>
        <Paragraph>
          Below the key metrics, you can customize which sections to display. Click on the <strong>settings icon</strong> (⚙️) 
          to choose up to 2 sections from the following options:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureDescription>
              • Top Performers - Best Distance
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Top Performers - Average Distance
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Planned Training Sessions
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Planned Competition Sessions
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Number of Jumps per Athlete
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Weight of Athletes
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Number of Training Sessions per Athlete
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Current Form of Athletes (Last 2 weeks)
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle>Jumper (Athlete) Dashboard</SubSectionTitle>
        <Paragraph>
          When logged in as an athlete (jumper), the Home dashboard shows your personal performance metrics:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureTitle>Personal Metrics</FeatureTitle>
            <FeatureDescription>
              View your total jumps, average distance, best distance, and total sessions. These metrics are calculated 
              from all your training sessions, competition sessions, and Banana Cup competitions.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Weight Tracking</FeatureTitle>
            <FeatureDescription>
              Track your weight over time by adding weight entries with dates. View your weight history in a table format 
              to monitor physical development.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Height Tracking</FeatureTitle>
            <FeatureDescription>
              Record your height measurements over time to track growth and physical development. View height history 
              to see progression.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Goals Management</FeatureTitle>
            <FeatureDescription>
              Set and track both coach goals and your personal goals for specific time periods. Mark goals as reached 
              when achieved and edit goals as needed to stay motivated and focused.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Trophy Cabinet</FeatureTitle>
            <FeatureDescription>
              View your competitive achievements including podium finishes (1st, 2nd, 3rd place) by category and a complete 
              list of all competitions where you achieved top 3 results. This provides a comprehensive view of your 
              competitive success.
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>
      </SectionWithId>

      {/* Athletes Section */}
      <SectionWithId id="athletes">
        <SectionTitle>
          <IconWrapper><Icon component={FiUsers} /></IconWrapper>
          Athletes Management
        </SectionTitle>
        
        <Paragraph>
          The Athletes section is your athlete database where you manage all athlete information, track their development, 
          and view detailed statistics.
        </Paragraph>

        <SubSectionTitle>Adding New Athletes</SubSectionTitle>
        <Paragraph>
          To add a new athlete, navigate to the <strong>Athletes</strong> section and click "Add New Athlete". You'll need to provide 
          the athlete's personal information: first name, last name, club affiliation, and gender. 
          After saving, the system automatically generates a unique <strong>Athlete ID</strong> for the athlete.
        </Paragraph>
        <Paragraph>
          Once you've created an athlete record, you can share the Athlete ID with the athlete so they can connect their account. 
          For detailed instructions on how coaches and athletes connect their accounts,{' '}
          <a 
            href="#connecting-accounts" 
            onClick={(e) => {
              e.preventDefault();
              scrollToSection('connecting-accounts');
            }}
            style={{ 
              color: 'var(--accent-orange)', 
              textDecoration: 'underline',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            see the "Connecting Coach and Athlete Accounts" section
          </a>
          .
        </Paragraph>

        <SubSectionTitle>Connecting Athletes with ID</SubSectionTitle>
        <Paragraph>
          If an athlete has already created their account before you added them to your system, you can connect to their existing account 
          using their Athlete ID. Navigate to the <strong>Athletes</strong> section and click "Connect by ID". Enter the Athlete ID provided by the athlete.
        </Paragraph>
        <Paragraph>
          Athletes can find their <strong>Athlete ID</strong> in <strong>Account Settings</strong> - In the Profile tab, 
          the Athlete ID is displayed in the personal information section.
        </Paragraph>
        <Paragraph>
          For complete step-by-step instructions on connecting coach and athlete accounts,{' '}
          <a 
            href="#connecting-accounts" 
            onClick={(e) => {
              e.preventDefault();
              scrollToSection('connecting-accounts');
            }}
            style={{ 
              color: 'var(--accent-orange)', 
              textDecoration: 'underline',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            see the "Connecting Coach and Athlete Accounts" section
          </a>
          .
        </Paragraph>

        <SubSectionTitle>Athlete Statistics</SubSectionTitle>
        <Paragraph>
          Each athlete's profile displays comprehensive statistics:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureDescription>
              • <strong>Number of Jumps</strong> - Total jumps across all sessions (training and competition)
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • <strong>Number of Training Sessions</strong> - Count of training sessions participated in
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • <strong>Number of Competition Sessions</strong> - Count of competitions participated in
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • <strong>Average Distance</strong> - Calculated average of all jump distances
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle>Athlete Detail Page</SubSectionTitle>
        <Paragraph>
          Click on any athlete to view their detailed profile page, which includes:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureDescription>
              • Complete personal information and statistics
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Weight history - List of all imported weights over time with dates
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Height history - List of all imported heights over time with dates
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • All training sessions the athlete has participated in
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Goal tracking with coach and athlete goals
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Trophy Cabinet - View podium finishes (1st, 2nd, 3rd place) by category and all competitions where the athlete achieved top 3 results
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Quick access to edit athlete information
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle>Trophy Cabinet</SubSectionTitle>
        <Paragraph>
          The Trophy Cabinet feature displays an athlete's competitive achievements:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureTitle>Podium Counts by Category</FeatureTitle>
            <FeatureDescription>
              See how many times the athlete achieved 1st, 2nd, or 3rd place for each competition category. Only categories 
              where the athlete has achieved at least one podium finish are shown.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Top 3 Competitions List</FeatureTitle>
            <FeatureDescription>
              View a complete list of all competitions where the athlete's result was in the top 3, including date, category, 
              location, hill size, and position. This provides a comprehensive view of competitive success.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Competition Sessions Only</FeatureTitle>
            <FeatureDescription>
              Trophy Cabinet results come only from Competition Sessions, not from Banana Cup sessions. This ensures that 
              only official competition results are counted.
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>
      </SectionWithId>

      {/* Training Sessions Section */}
      <SectionWithId id="training-sessions">
        <SectionTitle>
          <IconWrapper><Icon component={FiActivity} /></IconWrapper>
          Training Sessions
        </SectionTitle>
        
        <Paragraph>
          The Training Sessions section is where you record and manage all your training activities. This is one of the 
          most frequently used sections in sjMate.
        </Paragraph>

        <SubSectionTitle>Creating a New Training Session</SubSectionTitle>
        <Paragraph>
          To start a new training session:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureDescription>
              1. Click "Start New Training Session" button
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              2. Select the training type: <strong>Ski Jumping</strong>, <strong>Ground Training</strong>, or <strong>Cross Country</strong>
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              3. Fill in the date, start time, end time, and location
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              4. Select weather conditions (optional but recommended for analysis)
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              5. Select one or more athletes who will participate
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              6. Click "Start Training Session"
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle>During the Session</SubSectionTitle>
        <Paragraph>
          Once a session is active, you can:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureTitle>Add Jumps (Ski Jumping Sessions)</FeatureTitle>
            <FeatureDescription>
              Click "Import Jump" to record each jump. Fill in distance, starting gate, wind, speed, telemark landing, 
              equipment used, and rating. Use the camera icon to record a video of the jump.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Add Ground Training Records</FeatureTitle>
            <FeatureDescription>
              For ground training sessions, record the training type (coordination, power, speed, stretching, specialized 
              coordination, imitation ski jump, run, or visualization) and intensity level (1-10) for each athlete.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Add Coach Notes</FeatureTitle>
            <FeatureDescription>
              Use the "Coach's Description / Notes" field to document observations, feedback, or important information about 
              the training session. This is especially useful for later review and analysis.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Pause/Resume Session</FeatureTitle>
            <FeatureDescription>
              If you need to take a break, you can pause the session and resume it later. This is useful for longer training 
              sessions or when you need to step away temporarily.
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle>Viewing Jump Records</SubSectionTitle>
        <Paragraph>
          All recorded jumps are displayed in a comprehensive table showing:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureDescription>
              • Jump number, athlete name, starting gate, distance, wind, speed
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Telemark landing status, hill size, equipment used (suit, skis, shoes)
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Video link (if recorded) - click "Watch" to view the jump video on Google Drive
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Edit button to modify jump data if needed
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle>Session Management</SubSectionTitle>
        <Paragraph>
          You can sort jumps by any column, filter by athlete, and edit or delete jumps as needed. When you're finished 
          with the session, click "Finish Training Session" to complete it.
        </Paragraph>
      </SectionWithId>

      {/* Competition Sessions Section */}
      <SectionWithId id="competition-sessions">
        <SectionTitle>
          <IconWrapper><Icon component={FiAward} /></IconWrapper>
          Competition Sessions
        </SectionTitle>
        
        <Paragraph>
          Competition Sessions allow you to record and analyze competition results, including qualification rounds and 
          final competitions.
        </Paragraph>

        <SubSectionTitle>Creating a Competition Session</SubSectionTitle>
        <Paragraph>
          To import competition results:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureDescription>
              1. Click either "Import Competition Results" or "Import Qualification Results"
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              2. Fill in competition details: date, start time, end time, location, weather, hill size
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              3. Select competition category: <strong>Olympic Games</strong>, <strong>World Championships</strong>, 
              <strong>World Cup</strong>, <strong>Summer Grand Prix</strong>, <strong>Continental Cup</strong>, 
              <strong>FIS Cup</strong>, <strong>Alpen Cup</strong>, <strong>National Championships</strong>, 
              <strong>National Cup</strong>, <strong>Junior World Championships</strong>, 
              <strong>Youth Olympic Games</strong>, <strong>EYOF</strong>, or <strong>OPA Games</strong>
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              4. For National Championships competitions, select the age category (Adults, U20, U18, U16, etc.)
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              5. Select participating athletes
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              6. Click "Start Importing Competition Results"
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle>Recording Competition Jumps</SubSectionTitle>
        <Paragraph>
          Competition jumps have more detailed data than training jumps:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureTitle>Trial Round (Optional)</FeatureTitle>
            <FeatureDescription>
              Record trial round data including starting gate, distance, wind, speed, telemark, and equipment used.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>First Round</FeatureTitle>
            <FeatureDescription>
              Record all first round data: starting gate, distance, wind conditions, speed, telemark landing, and equipment. 
              This is mandatory for competition results.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Second Round</FeatureTitle>
            <FeatureDescription>
              Record second round data with the same details as the first round. The system automatically calculates the 
              final result based on both rounds.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Result and Rating</FeatureTitle>
            <FeatureDescription>
              Enter the final competition result (position) and add a rating (1-10) for the overall performance. You can 
              also record separate videos for each round using the camera icon.
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle>Competition Data Analysis</SubSectionTitle>
        <Paragraph>
          All competition data is stored and can be analyzed in the Competition Sessions Analytics section. You can track 
          performance trends, compare results across different competitions, and identify areas for improvement.
        </Paragraph>
      </SectionWithId>

      {/* Equipment Section */}
      <SectionWithId id="equipment">
        <SectionTitle>
          <IconWrapper><Icon component={FiPackage} /></IconWrapper>
          Equipment Management
        </SectionTitle>
        
        <Paragraph>
          The Equipment section helps you manage all ski jumping equipment including suits, skis, shoes, and bindings. Proper 
          equipment tracking is essential for performance analysis and maintenance planning.
        </Paragraph>

        <SubSectionTitle>Adding Equipment</SubSectionTitle>
        <Paragraph>
          When adding new equipment:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureTitle>Equipment Type</FeatureTitle>
            <FeatureDescription>
              Select the type: <strong>Suit</strong>, <strong>Skis</strong>, <strong>Shoes</strong>, or <strong>Bindings</strong>. Each type serves 
              a different purpose and is tracked separately.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Equipment Details</FeatureTitle>
            <FeatureDescription>
              Enter the equipment name, brand, and size. This information helps you identify and organize your equipment 
              inventory.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Athlete Assignment</FeatureTitle>
            <FeatureDescription>
              Optionally assign equipment to a specific athlete. If left unassigned, the equipment is available for use 
              by any athlete during sessions.
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle>Jump Count Tracking</SubSectionTitle>
        <Paragraph>
          The system automatically tracks how many jumps each piece of equipment has been used for. This is important for:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureDescription>
              • Monitoring equipment wear and planning replacements
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Analyzing equipment performance (which equipment produces better results)
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Ensuring fair equipment usage across athletes
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle>Equipment Organization</SubSectionTitle>
        <Paragraph>
          Equipment is organized by athlete, making it easy to see all equipment assigned to each athlete. Within each 
          athlete's section, equipment is separated by type (Suits, Skis, Shoes, and Bindings) with clear visual indicators. 
          This organization helps you:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureDescription>
              • Quickly see which equipment belongs to which athlete
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Track equipment usage per athlete
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Manage equipment assignments efficiently
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>
        <Paragraph>
          Use the single "Add Equipment" button to add new equipment. Select the equipment type, enter details, and assign 
          it to an athlete. The system automatically organizes it in the correct section.
        </Paragraph>
      </SectionWithId>

      {/* Training Planner Section */}
      <SectionWithId id="training-planner">
        <SectionTitle>
          <IconWrapper><Icon component={FiCalendar} /></IconWrapper>
          Training Planner
        </SectionTitle>
        
        <Paragraph>
          The Training Planner allows you to schedule and organize future training sessions in advance. This helps you 
          plan your training program and ensures all sessions are properly documented.
        </Paragraph>

        <SubSectionTitle>Creating Training Plans</SubSectionTitle>
        <Paragraph>
          To create a new training plan:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureDescription>
              1. Click "Create New Training Plan"
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              2. Select training type (Ski Jumping, Ground Training, Cross Country, Competition, or Banana Cup)
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              3. Set the date, start time, and end time
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              4. Enter the location
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              5. Select participating athletes
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              6. For Banana Cup sessions, select the specific Banana Cup competition
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              7. Click "Create Training Plan"
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle>Managing Plans</SubSectionTitle>
        <Paragraph>
          Once created, training plans appear in your calendar view. You can:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureDescription>
              • View all planned sessions in chronological order
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Edit plans to modify details before the session occurs
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Delete plans if they need to be cancelled
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Start a session directly from the plan when it's time to begin
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle>Banana Cup Integration</SubSectionTitle>
        <Paragraph>
          The Training Planner integrates with Banana Cup competitions. When creating a Banana Cup session, you can link 
          it to a specific Banana Cup competition, making it easy to organize and track internal competitions.
        </Paragraph>
      </SectionWithId>

      {/* Banana Cup Section */}
      <SectionWithId id="banana-cup">
        <SectionTitle>
          <IconWrapper>🍌</IconWrapper>
          Banana Cup - Internal Competitions
        </SectionTitle>
        
        <Paragraph>
          Banana Cup is a feature for creating and managing internal competitions within your team. It includes a point 
          system and allows you to track performance across multiple competitions.
        </Paragraph>

        <SubSectionTitle>Creating a Banana Cup</SubSectionTitle>
        <Paragraph>
          To create a new Banana Cup:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureDescription>
              1. Click "Create New Banana Cup"
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              2. Enter a name for the Banana Cup (e.g., "Winter 2024 Banana Cup")
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              3. Set the number of competitions (how many individual competitions will be part of this cup)
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              4. Select participating athletes
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              5. Optionally add a punishment description (for the last-place finisher)
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle>Managing Competitions</SubSectionTitle>
        <Paragraph>
          Each Banana Cup contains multiple competitions. For each competition, you can:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureTitle>Set Competition Details</FeatureTitle>
            <FeatureDescription>
              Enter the date, start time, end time, location, weather conditions, and hill size for each competition.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Record Jumps</FeatureTitle>
            <FeatureDescription>
              Record jumps for each round (first, second, third, fourth, or fifth round depending on the competition format). 
              Each jump includes distance, wind, speed, telemark, starting gate, and equipment information.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Finalize Competition</FeatureTitle>
            <FeatureDescription>
              Once all jumps are recorded, click "Finalize Competition" to calculate results. The system automatically 
              calculates total distance (sum of all rounds), adds telemark bonuses (+2 points per telemark), adjusts for 
              wind, and ranks athletes by final result.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Point System</FeatureTitle>
            <FeatureDescription>
              Points are awarded based on position: 1st place = 100 points, 2nd = 80, 3rd = 60, and so on down to 30th 
              place = 1 point. Points accumulate across all competitions in the Banana Cup.
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle>How Final Results and Points Are Calculated</SubSectionTitle>
        <Paragraph>
          So you know exactly how we get to the numbers:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureTitle>Per-athlete result in a competition</FeatureTitle>
            <FeatureDescription>
              For each athlete, the system first computes a <strong>total result</strong>: the sum of all round distances (e.g. round 1 + round 2 + …). 
              Then it adds <strong>wind adjustment</strong> (each round’s wind in m/s; positive = tailwind, negative = headwind) and 
              <strong>+2 points for each round with telemark YES</strong>. Athletes are ranked by this total (highest first).
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Points in this competition</FeatureTitle>
            <FeatureDescription>
              Position in the ranking gives points: 1st = 100, 2nd = 80, 3rd = 60, 4th = 50, 5th = 45, then 40, 36, 32, 30, 28, … down to 30th = 1. 
              Positions below 30th get 0 points. This is the same table used in the app.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Banana Cup standings</FeatureTitle>
            <FeatureDescription>
              Each athlete’s <strong>total points in the cup</strong> are the sum of their competition points from all competitions in that Banana Cup. 
              The standings show who has the most points across the cup.
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle>Tracking Results</SubSectionTitle>
        <Paragraph>
          The Banana Cup dashboard shows:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureDescription>
              • Current standings with total points for each athlete
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Individual competition results
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Progress through the cup (which competitions are completed)
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>
      </SectionWithId>

      {/* Analytics Section */}
      <SectionWithId id="analytics">
        <SectionTitle>
          <IconWrapper><Icon component={FiTrendingUp} /></IconWrapper>
          Analytics - Performance Insights
        </SectionTitle>
        
        <Paragraph>
          The Analytics section provides comprehensive performance analysis tools to help you understand trends, identify 
          strengths and weaknesses, and make data-driven decisions. All analytics support date range filtering and 
          athlete selection.
        </Paragraph>

        <SubSectionTitle id="analytics-training">Training Sessions Analytics</SubSectionTitle>
        <Paragraph>
          Analyze training session frequency and patterns:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureTitle>Sessions Tab</FeatureTitle>
            <FeatureDescription>
              View training session frequency over time with line charts. See how training intensity varies by month, 
              week, or custom date ranges. Identify periods of high and low activity.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Location Tab</FeatureTitle>
            <FeatureDescription>
              Analyze where training sessions are conducted. See which locations are used most frequently and how 
              performance varies by location.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Athlete Tab</FeatureTitle>
            <FeatureDescription>
              Compare training session participation across athletes. Identify who trains most frequently and track 
              individual training patterns.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Training Type Distribution</FeatureTitle>
            <FeatureDescription>
              View pie charts showing the distribution of different training types (Ski Jumping, Ground Training, 
              Cross Country) to ensure balanced training programs.
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle id="analytics-competition">Competition Sessions Analytics</SubSectionTitle>
        <Paragraph>
          Track and analyze competition performance:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureDescription>
              • View competition frequency and results over time
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Analyze performance by competition category (Olympic Games, World Championships, World Cup, National Championships, etc.)
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • For high-level competitions (Olympic Games, World Championships, Junior World Championships, Youth Olympic Games, EYOF), 
              view "Best Result" instead of points in standings
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Track results and identify trends in competition performance
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Compare competition results across different time periods
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle id="analytics-distance">Distance Analytics</SubSectionTitle>
        <Paragraph>
          One of the most important analytics for ski jumping - track jump distances:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureTitle>Distance Trends</FeatureTitle>
            <FeatureDescription>
              View line charts showing distance progression over time for individual athletes or the entire team. 
              Identify improvement trends and periods of peak performance.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Distance Distribution</FeatureTitle>
            <FeatureDescription>
              See bar charts showing the distribution of jump distances. Understand the range of performance and 
              identify consistency levels.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Athlete Comparison</FeatureTitle>
            <FeatureDescription>
              Compare distance performance across multiple athletes. See who performs best and identify learning 
              opportunities.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Hill Size Analysis</FeatureTitle>
            <FeatureDescription>
              Analyze performance by hill size to understand how athletes perform on different hills and identify 
              optimal training conditions.
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle id="analytics-speed">Inrun Speed Analytics</SubSectionTitle>
        <Paragraph>
          Analyze speed data to optimize takeoff performance:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureTitle>Speed Trends</FeatureTitle>
            <FeatureDescription>
              Track speed progression over time. Higher speeds generally correlate with longer distances, so monitoring 
              speed trends helps identify technical improvements.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Speed vs Distance</FeatureTitle>
            <FeatureDescription>
              Analyze the relationship between inrun speed and jump distance. This helps identify optimal speed ranges 
              and understand the speed-distance correlation for each athlete.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Athlete Speed Comparison</FeatureTitle>
            <FeatureDescription>
              Compare speed performance across athletes to identify who achieves the best speeds and understand 
              individual speed characteristics.
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle id="analytics-weight">Weight Analytics</SubSectionTitle>
        <Paragraph>
          Monitor athlete physical development through weight tracking:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureTitle>Weight Progression</FeatureTitle>
            <FeatureDescription>
              View line charts showing weight changes over time for each athlete. Track growth, weight management, 
              and physical development.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Weight vs Performance</FeatureTitle>
            <FeatureDescription>
              Analyze the relationship between weight and performance metrics (distance, speed) to understand optimal 
              weight ranges for peak performance.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Athlete Weight Comparison</FeatureTitle>
            <FeatureDescription>
              Compare weight trends across athletes to ensure healthy development and identify any concerns.
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle id="analytics-equipment">Equipment Analytics</SubSectionTitle>
        <Paragraph>
          Analyze equipment performance and usage:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureTitle>Equipment Performance</FeatureTitle>
            <FeatureDescription>
              Compare performance metrics (average distance, speed) for different equipment combinations. Identify 
              which suits, skis, and shoes produce the best results.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Equipment Usage</FeatureTitle>
            <FeatureDescription>
              Track how often each piece of equipment is used and monitor jump counts. This helps plan equipment 
              maintenance and replacements.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Equipment Trends</FeatureTitle>
            <FeatureDescription>
              View performance trends for specific equipment over time to understand wear effects and optimal usage periods.
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle id="analytics-ground">Ground Training Analytics</SubSectionTitle>
        <Paragraph>
          Analyze ground training activities and their impact:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureTitle>Training Type Distribution</FeatureTitle>
            <FeatureDescription>
              See which types of ground training (coordination, power, speed, stretching, etc.) are performed most 
              frequently and ensure a balanced training program.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Intensity Analysis</FeatureTitle>
            <FeatureDescription>
              Track intensity levels over time to monitor training load and ensure appropriate progression.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Training Frequency</FeatureTitle>
            <FeatureDescription>
              Analyze how often ground training sessions are conducted and identify patterns in training schedules.
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle id="analytics-jumper">Jumper Analytics - Personal Performance</SubSectionTitle>
        <Paragraph>
          Athletes (jumpers) have access to a dedicated Analytics section that provides personalized insights into their 
          own performance:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureTitle>Automatic Data Filtering</FeatureTitle>
            <FeatureDescription>
              All analytics automatically show only your data. There's no need to select yourself - the system knows 
              you're viewing your own performance metrics.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Simplified Interface</FeatureTitle>
            <FeatureDescription>
              The "Select Athletes" dropdown is removed for a cleaner, more focused interface. You can concentrate on 
              analyzing your own performance without distractions.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Location-Based Distance Analysis</FeatureTitle>
            <FeatureDescription>
              View "Best Distance for each Location" and "Average Distances for Each Location" side-by-side to understand 
              how you perform at different training locations. This helps identify your best-performing locations and 
              areas for improvement.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Personalized Graphs</FeatureTitle>
            <FeatureDescription>
              All graphs are tailored to show your data only. Compare your performance across different time periods, 
              locations, and conditions to identify trends and improvements.
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>Complete Analytics Access</FeatureTitle>
            <FeatureDescription>
              Access all analytics sections including Distance, Equipment, Weight/Height, Inrun Speed, Rating/Form, 
              Gate Number, Training Sessions, Competition Sessions, Ground Training, and Cross Country - all showing 
              your personal data.
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle>Using Analytics Effectively</SubSectionTitle>
        <Paragraph>
          To get the most out of the Analytics section:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureDescription>
              • Use date range filters to analyze specific time periods (last 7 days, last 30 days, this month, 
              this season, or custom ranges)
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Coaches: Select specific athletes to focus on individual performance analysis
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Jumpers: Your data is automatically filtered - just select date ranges and other filters to analyze 
              specific periods
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Compare different time periods to identify improvements or areas of concern
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Use multiple analytics sections together to get a comprehensive view of performance
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Export data or take screenshots to share with coaches and for personal review
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>
      </SectionWithId>

      {/* AI Chat Section */}
      <SectionWithId id="ai-chat">
        <SectionTitle>
          <IconWrapper><Icon component={FiZap} /></IconWrapper>
          AI Mate - Your Ski Jumping Assistant
        </SectionTitle>
        
        <Paragraph>
          AI Mate is your intelligent assistant powered by artificial intelligence, designed to help you with ski jumping 
          questions, training tips, performance improvement strategies, and technical guidance. Available for both coaches 
          and athletes (jumpers) with HS240 plan subscription.
        </Paragraph>

        <SubSectionTitle>Getting Started with AI Mate</SubSectionTitle>
        <Paragraph>
          To access AI Mate:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureDescription>
              1. Navigate to the <strong>AI Chat</strong> section from your dashboard sidebar
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              2. Ensure you have an active HS240 plan subscription (required for AI Chat access)
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              3. Start chatting by typing your question in the message input field
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle>How It Works</SubSectionTitle>
        <Paragraph>
          AI Mate uses advanced AI technology to provide intelligent responses:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureDescription>
              • <strong>Conversation History:</strong> AI Mate remembers your conversation context, so you can have 
              natural, flowing discussions about your questions
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • <strong>Markdown Support:</strong> Responses are formatted with markdown, making them easy to read with 
              proper formatting, lists, and emphasis
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • <strong>Real-time Responses:</strong> Get instant answers to your questions without waiting
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • <strong>Keyboard Shortcuts:</strong> Press Enter to send messages, Shift+Enter for new lines
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle>Best Practices</SubSectionTitle>
        <Paragraph>
          To get the most out of AI Mate:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureDescription>
              • <strong>Be Specific:</strong> Ask detailed questions to get more targeted and useful answers
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • <strong>Follow Up:</strong> Ask follow-up questions to dive deeper into topics that interest you
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • <strong>Use Context:</strong> Reference your training data or specific situations when asking questions 
              for more personalized advice
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • <strong>Explore Topics:</strong> Don't hesitate to ask about any aspect of ski jumping - from basics 
              to advanced techniques
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>
      </SectionWithId>

      {/* Tips and Best Practices */}
      <SectionWithId id="tips">
        <SectionTitle>
          <IconWrapper><Icon component={FiBarChart2} /></IconWrapper>
          Tips and Best Practices
        </SectionTitle>
        
        <SubSectionTitle>Data Entry Best Practices</SubSectionTitle>
        <FeatureList>
          <FeatureItem>
            <FeatureDescription>
              • <strong>Be Consistent:</strong> Enter data immediately after each session while details are fresh in your mind
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • <strong>Record Everything:</strong> Even small details like wind conditions and equipment choices can 
              provide valuable insights later
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • <strong>Add Coach Notes:</strong> Document observations, feedback, and important information in the 
              coach description field
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle>Organization Tips</SubSectionTitle>
        <FeatureList>
          <FeatureItem>
            <FeatureDescription>
              • <strong>Plan Ahead:</strong> Use the Training Planner to schedule sessions in advance and ensure 
              proper preparation
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • <strong>Keep Equipment Updated:</strong> Regularly update equipment information and track usage to 
              plan maintenance and replacements
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • <strong>Review Analytics Regularly:</strong> Check analytics weekly or monthly to identify trends and 
              adjust training programs accordingly
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>

        <SubSectionTitle>Getting Help</SubSectionTitle>
        <Paragraph>
          If you need assistance or have questions about using sjMate:
        </Paragraph>
        <FeatureList>
          <FeatureItem>
            <FeatureDescription>
              • Refer back to this "How to Start" guide anytime by clicking the question mark icon
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Check the Account Settings to configure your Google Drive folder for video uploads
            </FeatureDescription>
          </FeatureItem>
          <FeatureItem>
            <FeatureDescription>
              • Explore each section gradually - you don't need to use every feature immediately
            </FeatureDescription>
          </FeatureItem>
        </FeatureList>
      </SectionWithId>

      {/* Conclusion */}
      <Section>
        <SectionTitle>Ready to Get Started?</SectionTitle>
        <Paragraph>
          You now have a comprehensive understanding of how sjMate works. Start by adding your athletes and equipment, 
          then begin recording training sessions. As you use the system more, you'll discover additional features and 
          insights that will help you optimize your coaching and improve athlete performance.
        </Paragraph>
        <Paragraph>
          Remember: The more data you enter, the more valuable the analytics become. Consistent data entry is key to 
          getting the most out of sjMate.
        </Paragraph>
        <HighlightBox>
          <HighlightText>
            <strong>🎯 Next Steps:</strong> Go to your dashboard and start with Step 1 - Add your first athlete! 
            The system will guide you through the rest as you use it.
          </HighlightText>
        </HighlightBox>
      </Section>
      </ContentWrapper>
      <ScrollToTopButton 
        onClick={scrollToTop}
        className={showScrollTop ? 'visible' : ''}
        aria-label="Scroll to top"
      >
        <Icon component={FiArrowUp} />
      </ScrollToTopButton>
    </Container>
  );
};

export default HowToStart;

